import { useNavigate, useSearchParams } from 'react-router-dom'
import { getUser, logout } from '../auth'
import { useEffect, useRef, useState, type ReactNode } from 'react'

interface TopBarProps {
  onMenuClick: () => void
  breadcrumb?: string
  actions?: ReactNode
}

export function TopBar({ onMenuClick, breadcrumb, actions }: TopBarProps) {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const user = getUser()
  const initials = user
    ? `${user.first_name?.[0] || ''}${user.last_name?.[0] || ''}`.toUpperCase()
    : '?'

  // Two-way bind to ?q= URL param. Local state holds the in-flight
  // input so typing stays smooth; we debounce-commit into the URL so
  // the dashboard query re-runs without a roundtrip per keystroke.
  const initialQ = searchParams.get('q') || ''
  const [query, setQuery] = useState(initialQ)
  const searchRef = useRef<HTMLInputElement | null>(null)

  // Sync external URL → local input when ?q= changes (e.g. navigation).
  useEffect(() => {
    const urlQ = searchParams.get('q') || ''
    if (urlQ !== query) setQuery(urlQ)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams])

  // Debounce: commit query → URL after 250ms idle. Skips if unchanged.
  useEffect(() => {
    const handle = window.setTimeout(() => {
      const current = searchParams.get('q') || ''
      if (current === query) return
      const next = new URLSearchParams(searchParams)
      if (query.trim()) next.set('q', query.trim())
      else next.delete('q')
      setSearchParams(next, { replace: true })
    }, 250)
    return () => window.clearTimeout(handle)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query])

  // ⌘K / Ctrl+K focuses the search input.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault()
        searchRef.current?.focus()
        searchRef.current?.select()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  return (
    <header className="topbar">
      <button className="topbar-btn" onClick={onMenuClick} style={{ border: 'none', padding: '4px 8px' }}>
        <i className="fas fa-bars" />
      </button>
      <a href="/" className="topbar-logo" onClick={(e) => { e.preventDefault(); navigate('/'); }}>Content</a>
      {breadcrumb && (
        <div className="topbar-breadcrumb">
          <span className="sep">/</span>
          <span>{breadcrumb}</span>
        </div>
      )}
      <div className="topbar-spacer" />
      {actions}
      <label className="topbar-search">
        <i className="fas fa-search" style={{ fontSize: 12 }} />
        <input
          ref={searchRef}
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              setQuery('')
              ;(e.target as HTMLInputElement).blur()
            }
          }}
          placeholder="Search…"
          style={{
            border: 'none',
            background: 'transparent',
            outline: 'none',
            fontSize: 13,
            width: 140,
            color: 'inherit',
          }}
        />
        <kbd>&#8984;K</kbd>
      </label>
      <div
        className="topbar-avatar"
        title={user ? `${user.first_name} ${user.last_name}\n${user.email}` : ''}
        onClick={() => { logout(); navigate('/login'); }}
        style={{ cursor: 'pointer' }}
      >
        {initials}
      </div>
    </header>
  )
}
